import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Mail, Send, ShieldAlert, Wand2 } from 'lucide-react';
import { createSecret, emailSecret } from '@/api/secrets';
import { searchUsers } from '@/api/users';
import { ApiError } from '@/api/client';
import { RequireRole } from '@/auth/RequireRole';
import { PasswordGenerator } from '@/components/PasswordGenerator';
import { formatDateTime } from '@/lib/dateFormat';
import type { CreateSecretResponse, DirectoryMember } from '@/types/oscar';
import {
  createSecretSchema,
  defaultExpiryDate,
  expiryDateToIsoUtc,
  maxExpiryDate,
  minExpiryDate,
  type CreateSecretFormData,
} from './schema';
import { OnPassTabs } from './OnPassTabs';
import { CopyButton } from './CopyButton';

function buildDefaults(): CreateSecretFormData {
  return {
    Secret: '',
    // Blank by default → unlimited views until expiry. The creator sets a number
    // (e.g. 1 for a classic one-time link) if they want a view cap.
    MaxViews: undefined,
    ExpiresAt: defaultExpiryDate(),
    Passphrase: '',
  };
}

export function OnPassCreate() {
  return (
    <section className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold text-navy">OnPass — share a secret</h1>
      <OnPassTabs />
      <RequireRole anyOf={['oscar-admin', 'oscar-helpdesk', 'onpass-user']}>
        <CreateForm />
      </RequireRole>
    </section>
  );
}

function CreateForm() {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<CreateSecretResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showGenerator, setShowGenerator] = useState(false);
  // Optional "lock to a user" — held in local state (a directory member, not a
  // scalar form field) and sent as LockedToUserId on submit.
  const [lockUser, setLockUser] = useState<DirectoryMember | null>(null);
  // Views choice: off (default) = unlimited until expiry; on = cap at a number.
  // Expiry is the primary control, so a view cap is an explicit opt-in rather
  // than a blank-with-placeholder that reads ambiguously.
  const [limitViews, setLimitViews] = useState(false);

  const form = useForm<CreateSecretFormData>({
    resolver: zodResolver(createSecretSchema),
    defaultValues: buildDefaults(),
  });
  const { register, handleSubmit, reset, setValue, formState } = form;

  const mutation = useMutation({
    mutationFn: (data: CreateSecretFormData) => {
      // Unless the creator opts into a view cap, the link is unlimited until
      // expiry. A capped-but-blank number falls back to 1 server-side.
      const unlimited = !limitViews;
      return createSecret({
        Secret: data.Secret,
        MaxViews: unlimited ? undefined : data.MaxViews,
        UnlimitedViews: unlimited || undefined,
        ExpiresAtUtc: expiryDateToIsoUtc(data.ExpiresAt),
        LockedToUserId: lockUser?.Id,
        Passphrase: data.Passphrase?.trim() ? data.Passphrase : undefined,
      });
    },
    onSuccess: (created) => {
      setResult(created);
      // Clear the plaintext from the form immediately — it is never echoed back.
      reset(buildDefaults());
      setLockUser(null);
      setLimitViews(false);
      queryClient.invalidateQueries({ queryKey: ['secrets'] });
    },
    onError: (err) => {
      setSubmitError(err instanceof ApiError ? err.message : 'Could not create the secret.');
    },
  });

  const onSubmit = (data: CreateSecretFormData) => {
    setSubmitError(null);
    setResult(null);
    mutation.mutate(data);
  };

  if (result) {
    return (
      <div>
        <div className="rounded-md border border-green/40 bg-green/10 p-4">
          <h2 className="font-heading text-lg font-bold text-navy">Your one-time link is ready</h2>
          <p className="mt-1 flex items-start gap-2 text-sm font-semibold text-red">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            You won&rsquo;t see this link again. Copy it now and share it with the recipient.
          </p>

          <div className="mt-3 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded border border-border bg-white px-2 py-1.5 font-mono text-sm">
              {result.Url}
            </code>
            <CopyButton value={result.Url} label="Copy link" />
          </div>

          <dl className="mt-3 grid grid-cols-2 gap-2 text-sm text-darkGrey">
            <div>
              <dt className="font-semibold">Views allowed</dt>
              <dd>{result.MaxViews == null ? 'Unlimited (until it expires)' : result.MaxViews}</dd>
            </div>
            <div>
              <dt className="font-semibold">Expires</dt>
              <dd>{formatDateTime(result.ExpiresAtUtc)}</dd>
            </div>
            {result.LockedToName && (
              <div className="col-span-2">
                <dt className="font-semibold">Locked to</dt>
                <dd>{result.LockedToName} — only they can open the link.</dd>
              </div>
            )}
          </dl>
        </div>

        <EmailLinkPanel token={result.Token} />

        <button
          type="button"
          onClick={() => setResult(null)}
          className="mt-4 rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
        >
          Share another secret
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {submitError && (
        <div role="alert" className="rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {submitError}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-darkGrey">Secret</span>
          <button
            type="button"
            onClick={() => setShowGenerator((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy hover:underline"
          >
            <Wand2 className="h-3.5 w-3.5" aria-hidden />
            {showGenerator ? 'Hide generator' : 'Generate a password'}
          </button>
        </div>
        {showGenerator && (
          <div className="mt-2">
            <PasswordGenerator
              onUse={(v) => {
                setValue('Secret', v, { shouldValidate: true, shouldDirty: true });
                setShowGenerator(false);
              }}
            />
          </div>
        )}
        <textarea
          {...register('Secret')}
          rows={4}
          autoComplete="off"
          spellCheck={false}
          placeholder="Paste the credential or message to share"
          className={`${inputCls} font-mono`}
        />
        {formState.errors.Secret?.message && (
          <span className="mt-1 block text-xs font-normal text-red">{formState.errors.Secret.message}</span>
        )}
      </div>

      {/* Expiry is the primary control — the link always stops working on this date. */}
      <Field
        label="Expiry date"
        error={formState.errors.ExpiresAt?.message}
        hint="The link stops working after this date. This is the main limit."
      >
        <input
          type="date"
          min={minExpiryDate()}
          max={maxExpiryDate()}
          {...register('ExpiresAt')}
          className={inputCls}
        />
      </Field>

      {/* Views cap is a secondary, explicit opt-in (off = unlimited until expiry). */}
      <fieldset className="rounded border border-border p-3">
        <legend className="px-1 text-xs font-semibold text-darkGrey">Number of views</legend>
        <label className="flex items-center gap-2 text-sm text-darkGrey">
          <input
            type="checkbox"
            checked={limitViews}
            onChange={(e) => {
              const on = e.target.checked;
              setLimitViews(on);
              // Seed a sensible cap when turning the limit on; clear it when off.
              setValue('MaxViews', on ? 1 : undefined, { shouldValidate: false });
            }}
          />
          Limit the number of views
        </label>
        {!limitViews ? (
          <p className="mt-1 text-xs font-normal text-darkGrey/70">
            Unlimited views until the expiry date above.
          </p>
        ) : (
          <div className="mt-2">
            <Field label="Maximum views" error={formState.errors.MaxViews?.message}>
              <input
                type="number"
                min={1}
                max={100}
                {...register('MaxViews')}
                className={inputCls}
              />
            </Field>
          </div>
        )}
      </fieldset>

      <Field
        label="Lock to a user (optional)"
        hint="Only this person, signed in to Oscar, can open the link. Leave blank for any signed-in staff member with the link."
      >
        <div className="mt-1">
          <RecipientPicker value={lockUser} onChange={setLockUser} />
        </div>
      </Field>

      <Field
        label="Passphrase (optional)"
        error={formState.errors.Passphrase?.message}
        hint="If set, the recipient must enter it to view the secret."
      >
        <input
          type="text"
          autoComplete="off"
          {...register('Passphrase')}
          placeholder="Leave blank for no passphrase"
          className={inputCls}
        />
      </Field>

      <button
        type="submit"
        disabled={mutation.isPending}
        className="rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
      >
        {mutation.isPending ? 'Creating…' : 'Create one-time link'}
      </button>
    </form>
  );
}

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

/**
 * Email-this-link panel shown on the OnPass result screen. Lets the creator
 * send the one-time link to a chosen Entra member with an optional note. The
 * server builds the canonical link and re-validates the recipient (enabled
 * member) before sending; the secret is never emailed.
 */
function EmailLinkPanel({ token }: { token: string }) {
  const [recipient, setRecipient] = useState<DirectoryMember | null>(null);
  const [message, setMessage] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => emailSecret(token, recipient!.Id, message),
    onSuccess: (res) => { setSentTo(res.Recipient); setError(null); },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not send the email.'),
  });

  if (sentTo) {
    return (
      <div className="mt-4 rounded-md border border-green/40 bg-green/10 p-4 text-sm text-darkGrey">
        <p className="flex items-center gap-2 font-semibold text-green">
          <Check className="h-4 w-4 shrink-0" aria-hidden /> Link emailed to {sentTo}.
        </p>
        <button
          type="button"
          onClick={() => { setSentTo(null); setRecipient(null); setMessage(''); }}
          className="mt-2 text-xs font-semibold text-navy hover:underline"
        >
          Email to someone else
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border border-border bg-white p-4">
      <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-navy">
        <Mail className="h-4 w-4 shrink-0" aria-hidden /> Email this link
      </h3>
      <p className="mt-1 text-xs text-darkGrey/70">
        Send the one-time link to an Onsite staff member — they&rsquo;ll sign in to view it.
      </p>
      {error && (
        <div role="alert" className="mt-2 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {error}
        </div>
      )}
      <div className="mt-3 space-y-3">
        <label className="block text-xs font-semibold text-darkGrey">
          Recipient
          <div className="mt-1">
            <RecipientPicker value={recipient} onChange={setRecipient} />
          </div>
        </label>
        <label className="block text-xs font-semibold text-darkGrey">
          Message (optional)
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Add a note for the recipient"
            className={`${inputCls} font-normal`}
          />
        </label>
        <button
          type="button"
          disabled={!recipient || mutation.isPending}
          onClick={() => { setError(null); mutation.mutate(); }}
          className="inline-flex items-center gap-1.5 rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" aria-hidden />
          {mutation.isPending ? 'Sending…' : 'Send email'}
        </button>
      </div>
    </div>
  );
}

/**
 * Recipient combobox for OnPass — searches enabled Entra members via
 * /api/users/search (string ids, so a purpose-built variant rather than the
 * numeric-id EntityPicker). Free text never commits; only a pick does.
 */
function RecipientPicker({
  value,
  onChange,
}: {
  value: DirectoryMember | null;
  onChange: (m: DirectoryMember | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const results = useQuery({
    queryKey: ['onpass-users', debounced],
    queryFn: ({ signal }) => searchUsers(debounced, signal),
    enabled: open && debounced.length >= 2,
    retry: false,
  });

  const rows = results.data ?? [];
  const showMenu = open && debounced.length >= 2;

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded border border-border bg-white px-2 py-1.5 text-sm">
        <span className="font-medium text-navy">{value.DisplayName}</span>
        <span className="truncate text-xs text-darkGrey/60">{value.Email}</span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="ml-auto shrink-0 text-xs font-semibold text-orange hover:underline"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        placeholder="Search staff by name or email…"
        aria-label="Recipient"
        autoComplete="off"
        role="combobox"
        aria-expanded={showMenu}
        className={inputCls}
      />
      {showMenu && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded border border-border bg-white text-sm shadow-md">
          {results.isLoading && <li className="px-3 py-2 text-darkGrey/60">Searching…</li>}
          {results.isError && <li className="px-3 py-2 text-red">Lookup unavailable.</li>}
          {!results.isLoading && !results.isError && rows.length === 0 && (
            <li className="px-3 py-2 text-darkGrey/60">No matches for &ldquo;{debounced}&rdquo;.</li>
          )}
          {rows.map((m) => (
            <li key={m.Id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(m); setQuery(''); setOpen(false); }}
                className="block w-full px-3 py-1.5 text-left hover:bg-bg"
              >
                <span className="font-medium text-navy">{m.DisplayName}</span>
                <span className="ml-1.5 text-xs text-darkGrey/50">{m.Email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-xs font-semibold text-darkGrey">
      {label}
      {children}
      {hint && !error && <span className="mt-1 block font-normal text-darkGrey/70">{hint}</span>}
      {error && <span className="mt-1 block font-normal text-red">{error}</span>}
    </label>
  );
}
